import { create } from "zustand";
import { toast } from "react-toastify";
import {
  COMPLAINT_ENDPOINTS,
  getData,
  postData,
  putData,
} from "../../../../Apis/complaintApi";
import {
  COMPLAINT_MAX_FETCH_SIZE,
  paginateClientList,
  sortActiveFirst,
  buildClientPagination,
} from "../../utils/complaintListUtils";

const parseTimestamp = (item) => {
  const dateVal =
    item.createdDate ??
    item.createdOn ??
    item.createdAt ??
    item.modifiedDate ??
    item.modifiedOn ??
    item.updatedAt;

  if (dateVal) {
    const time = new Date(dateVal).getTime();
    if (!Number.isNaN(time)) return time;
  }

  const id = Number(
    item.grievanceCategoryId ?? item.grievancecategoryId ?? item.id
  );
  return Number.isNaN(id) ? 0 : id;
};

const sortGrievanceCategories = (list = []) => {
  return [...list].sort((a, b) => {
    // 1. Active items first, Inactive items after
    const activeA = Number(Boolean(a.isActive));
    const activeB = Number(Boolean(b.isActive));
    if (activeA !== activeB) {
      return activeB - activeA;
    }

    // 2. Latest added first within each group (Active or Inactive)
    const timeA = parseTimestamp(a);
    const timeB = parseTimestamp(b);
    if (timeA !== timeB) {
      return timeB - timeA;
    }

    // 3. Fallback to ID descending
    const idA = Number(a.grievanceCategoryId || 0);
    const idB = Number(b.grievanceCategoryId || 0);
    return idB - idA;
  });
};

const mapGrievanceCategoryItem = (item) => ({
  ...item,
  grievanceCategoryId:
    item.grievanceCategoryId ?? item.grievancecategoryId ?? item.id,
  categoryName:
    item.grievanceCategoryName ?? item.categoryName ?? item.name ?? "",
  slacategoryId: item.slacategoryId ?? item.slaCategoryId ?? "",
  slaCategoryType:
    item.slacategoryName ?? item.slaCategoryName ?? item.slaCategoryType ?? "",
  isActive: Boolean(item.isActive),
});

const mapGrievanceCategoryList = (response) => {
  const list = Array.isArray(response) ? response : response?.data ?? [];
  return list.map(mapGrievanceCategoryItem);
};

const mapSlaCategoryOption = (item) => {
  const id = item.slacategoryId ?? item.slaCategoryId;
  const name = item.name ?? item.categoryName ?? item.slacategoryName ?? "";
  const durationInHr = item.durationInHours ?? item.durationInHr ?? "";

  return {
    id,
    name,
    durationInHr,
    label: durationInHr ? `${name} (${durationInHr}hrs)` : name,
  };
};

const applyCategorySearch = (data, searchTerm) => {
  const term = searchTerm?.toLowerCase()?.trim();
  if (!term) return data;

  return data.filter((item) =>
    item?.categoryName?.toLowerCase()?.includes(term)
  );
};

export const useGrievanceCategoryStore = create((set, get) => ({
  grievanceCategories: [],
  filterOriginalGrievanceCategories: [],
  filteredGrievanceCategories: [],
  slaCategoryTypeOptions: [],
  isLoading: false,
  isSaving: false,
  filters: {
    searchTerm: "",
  },
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
  },

  updateFilter: (name, value) =>
    set((state) => ({
      filters: { ...state.filters, [name]: value },
    })),

  searchGrievanceCategoryHandler: (searchTerm) => {
    const { filterOriginalGrievanceCategories, pagination } = get();
    const filtered = applyCategorySearch(
      filterOriginalGrievanceCategories,
      searchTerm
    );
    const pagedRecords = paginateClientList(filtered, 1, pagination.pageSize);

    set({
      filters: { searchTerm },
      filteredGrievanceCategories: pagedRecords,
      pagination: buildClientPagination(
        filtered.length,
        1,
        pagination.pageSize
      ),
    });
  },

  resetFilters: () => {
    const { filterOriginalGrievanceCategories, pagination } = get();
    const pagedRecords = paginateClientList(
      filterOriginalGrievanceCategories,
      1,
      pagination.pageSize
    );

    set({
      filters: { searchTerm: "" },
      filteredGrievanceCategories: pagedRecords,
      pagination: buildClientPagination(
        filterOriginalGrievanceCategories.length,
        1,
        pagination.pageSize
      ),
    });
  },

  fetchSlaCategoryTypeOptions: async () => {
    try {
      const response = await getData(COMPLAINT_ENDPOINTS.GET_SLA_CATEGORIES, {
        PageIndex: 1,
        PageSize: 1000,
        IsActive: true,
      });

      set({
        slaCategoryTypeOptions: (response.data ?? [])
          .map(mapSlaCategoryOption)
          .filter((option) => option.id && option.name),
      });
    } catch {
      set({ slaCategoryTypeOptions: [] });
    }
  },

  fetchGrievanceCategories: async (options = {}) => {
    const { filters, pagination } = get();
    const currentPage = options.currentPage ?? pagination.currentPage;
    const pageSize = options.pageSize ?? pagination.pageSize;

    set({ isLoading: true });

    try {
      const response = await getData(
        COMPLAINT_ENDPOINTS.GET_GRIEVANCE_CATEGORIES,
        {
          PageIndex: 1,
          PageSize: COMPLAINT_MAX_FETCH_SIZE,
        }
      );

      const sorted = sortGrievanceCategories(mapGrievanceCategoryList(response));
      const filtered = applyCategorySearch(sorted, filters.searchTerm);
      const pagedRecords = paginateClientList(filtered, currentPage, pageSize);

      set({
        grievanceCategories: sorted,
        filterOriginalGrievanceCategories: sorted,
        filteredGrievanceCategories: pagedRecords,
        pagination: buildClientPagination(filtered.length, currentPage, pageSize),
        isLoading: false,
      });
      return { success: true, data: pagedRecords };
    } catch {
      set({
        grievanceCategories: [],
        filterOriginalGrievanceCategories: [],
        filteredGrievanceCategories: [],
        pagination: { ...get().pagination, totalCount: 0 },
        isLoading: false,
      });
      return { success: false };
    }
  },

  saveGrievanceCategory: async (payload, isEdit = false) => {
    set({ isSaving: true });

    try {
      if (isEdit) {
        await putData(COMPLAINT_ENDPOINTS.UPDATE_GRIEVANCE_CATEGORY, {
          grievanceCategoryId: payload.grievanceCategoryId,
          grievanceCategoryName: payload.categoryName,
          slacategoryId: payload.slacategoryId,
          isActive: payload.isActive,
        });
        toast.success("Grievance category updated successfully.");
      } else {
        await postData(COMPLAINT_ENDPOINTS.ADD_GRIEVANCE_CATEGORY, {
          grievanceCategoryName: payload.categoryName,
          slacategoryId: payload.slacategoryId,
          isActive: payload.isActive,
        });
        toast.success("Grievance category added successfully.");
      }

      await get().fetchGrievanceCategories();
      return { success: true };
    } catch (error) {
      const message =
        typeof error?.response?.data === "string"
          ? error.response.data
          : error?.response?.data?.message ||
            "Unable to save grievance category details.";
      toast.error(message);
      return { success: false };
    } finally {
      set({ isSaving: false });
    }
  },
}));
